/**
 * Shared helpers of the preview overlay providers (W2-02; ARCHITECTURE.md §7.4): per-frame lookups, line shapes on
 * screen (normal lines as their axis segment, textures / GIFs / text as their quad, invisible kinds as points),
 * line and note hit-testing, and label drawing. Everything works in the overlay canvas' device pixels.
 */

import type { LineDraw, LineStateRt, NoteQuadBatch } from '../../../core/engine/types';
import { Mat2 } from '../../../core/math/mat2d';
import { kindOfLine } from '../../../core/model/lineKind';
import { Units } from '../../../core/model/units';
import type { ChartDoc, DocIndexes, LineId, NoteId } from '../../../core/model/types';
import type { Mat2D, MessageRef, Vec2 } from '../../../core/util/types';
import type { OverlayDrawContext, OverlayHit } from '../../../render/types';
import { pointInQuad, pointSegmentDistance } from './gizmoMath';
import { overlayTheme } from './theme';

/** Pixel tolerance (CSS px) for picking lines and handles. */
export const PICK_TOLERANCE = 6;
/** World length a line label and a point marker use when a line has no visible extent. */
const POINT_RADIUS = 7;

export interface FrameInfo {
  readonly states: ReadonlyMap<LineId, LineStateRt>;
  readonly draws: ReadonlyMap<LineId, LineDraw>;
}

const infos = new WeakMap<OverlayDrawContext, FrameInfo>();

/** Per-frame lookups (built once per overlay context). */
export function frameInfo(c: OverlayDrawContext): FrameInfo {
  let info = infos.get(c);
  if (info === undefined) {
    const states = new Map<LineId, LineStateRt>();
    for (const s of c.lines) states.set(s.line, s);
    const draws = new Map<LineId, LineDraw>();
    for (const d of c.list.lines) draws.set(d.line, d);
    info = { states, draws };
    infos.set(c, info);
  }
  return info;
}

/** CSS px -> overlay device px. */
export function dp(c: Pick<OverlayDrawContext, 'dpr'>, cssPx: number): number {
  return cssPx * c.dpr;
}

export type LineShape =
  | { readonly kind: 'segment'; readonly a: Vec2; readonly b: Vec2; readonly halfThick: number }
  | { readonly kind: 'quad'; readonly pts: readonly number[] }
  | { readonly kind: 'point'; readonly p: Vec2 };

function quadOf(c: OverlayDrawContext, m: Mat2D, hx: number, hy: number): number[] {
  const out: number[] = [];
  for (const [x, y] of [
    [-hx, -hy],
    [hx, -hy],
    [hx, hy],
    [-hx, hy],
  ] as const) {
    const [wx, wy] = Mat2.apply(m, x, y);
    const [px, py] = c.worldToPx(wx, wy);
    out.push(px, py);
  }
  return out;
}

/** World units of one em of a text line before its scale (Phira: PxScale 0.04 W = 0.08 world units). */
const TEXT_EM = 0.08;

function textWidthWorld(c: OverlayDrawContext, text: string): number {
  const fontPx = Math.max(4, (TEXT_EM * c.viewport.W) / 2);
  const ctx = c.ctx;
  ctx.save();
  ctx.font = `${String(fontPx)}px ${overlayTheme().fontSans || 'sans-serif'}`;
  const w = ctx.measureText(text).width;
  ctx.restore();
  return (w / fontPx) * TEXT_EM;
}

/** The on-screen shape of a line (its drawable when Phira draws it, else what it would occupy). */
export function lineShape(
  c: OverlayDrawContext,
  s: LineStateRt,
  doc: ChartDoc | null,
  idx: DocIndexes | null,
): LineShape {
  const draw = frameInfo(c).draws.get(s.line);
  if (draw !== undefined) {
    switch (draw.kind) {
      case 'normal': {
        const a = Mat2.apply(draw.m, -draw.halfLen, 0);
        const b = Mat2.apply(draw.m, draw.halfLen, 0);
        // Thickness in world units after the drawable scale: measure the matrix' y column.
        const sy = Math.hypot(draw.m[2], draw.m[3]);
        const halfThick = Math.max(1, (draw.thickness * sy * c.viewport.W) / 4);
        return { kind: 'segment', a: c.worldToPx(a[0], a[1]), b: c.worldToPx(b[0], b[1]), halfThick };
      }
      case 'texture':
      case 'gif':
        return { kind: 'quad', pts: quadOf(c, draw.m, 0.5, 0.5) };
      case 'text': {
        const w = textWidthWorld(c, draw.text);
        return { kind: 'quad', pts: quadOf(c, draw.m, w / 2, TEXT_EM / 2) };
      }
      case 'paint':
        return { kind: 'point', p: c.worldToPx(s.x, s.y) };
    }
  }
  const centre = c.worldToPx(s.x, s.y);
  if (s.uiMover || s.drawsNothing === 'scale' || s.drawsNothing === 'emptyText') return { kind: 'point', p: centre };
  const line = doc?.lines.get(s.line);
  const kind = line !== undefined && doc !== null && idx !== null ? kindOfLine(doc, idx, line) : s.kind;
  const m = s.m;
  if (kind === 'normal') {
    const half = (doc?.pkg.lineLength ?? 6) * s.sx;
    const a = Mat2.apply(m, -half, 0);
    const b = Mat2.apply(m, half, 0);
    const thick = Units.thicknessPx(s.scaleYRaw) / 675;
    return {
      kind: 'segment',
      a: c.worldToPx(a[0], a[1]),
      b: c.worldToPx(b[0], b[1]),
      halfThick: Math.max(1, (thick * c.viewport.W) / 4),
    };
  }
  if ((kind === 'texture' || kind === 'gif') && line?.texture != null) {
    const meta = doc?.assets.get(line.texture)?.meta;
    const w = meta?.width ?? 0;
    const h = meta?.height ?? 0;
    if (w > 0 && h > 0) return { kind: 'quad', pts: quadOf(c, s.m, (w / 1350) * s.sx, (h / 1350) * s.sy) };
  }
  if (kind === 'text' && s.text !== null && s.text !== '') {
    const w = textWidthWorld(c, s.text) * s.sx;
    return { kind: 'quad', pts: quadOf(c, s.m, w / 2, (TEXT_EM / 2) * s.sy) };
  }
  return { kind: 'point', p: centre };
}

/** Distance (device px) from a point to a line shape; 0 inside a quad. */
export function shapeDistance(shape: LineShape, px: number, py: number): number {
  switch (shape.kind) {
    case 'segment':
      return Math.max(
        0,
        pointSegmentDistance(px, py, shape.a[0], shape.a[1], shape.b[0], shape.b[1]) - shape.halfThick,
      );
    case 'quad': {
      if (pointInQuad(px, py, shape.pts)) return 0;
      let d = Infinity;
      for (let i = 0; i < 4; i++) {
        const j = (i + 1) % 4;
        d = Math.min(
          d,
          pointSegmentDistance(
            px,
            py,
            shape.pts[i * 2] ?? 0,
            shape.pts[i * 2 + 1] ?? 0,
            shape.pts[j * 2] ?? 0,
            shape.pts[j * 2 + 1] ?? 0,
          ),
        );
      }
      return d;
    }
    case 'point':
      return Math.max(0, Math.hypot(px - shape.p[0], py - shape.p[1]) - POINT_RADIUS);
  }
}

/** Anchor (device px) of a shape: segment / quad centre, or the point. */
export function shapeCentre(shape: LineShape): Vec2 {
  switch (shape.kind) {
    case 'segment':
      return [(shape.a[0] + shape.b[0]) / 2, (shape.a[1] + shape.b[1]) / 2];
    case 'quad': {
      let x = 0;
      let y = 0;
      for (let i = 0; i < 4; i++) {
        x += shape.pts[i * 2] ?? 0;
        y += shape.pts[i * 2 + 1] ?? 0;
      }
      return [x / 4, y / 4];
    }
    case 'point':
      return shape.p;
  }
}

/** Draw order rank of a line: (zOrder, export index), larger = drawn later = on top. */
function drawRank(doc: ChartDoc | null, s: LineStateRt): readonly [number, number] {
  return [doc?.lines.get(s.line)?.zOrder ?? 0, s.index];
}

export interface LineHitOptions {
  readonly doc: ChartDoc | null;
  readonly idx: DocIndexes | null;
  /** Lines Phira draws nothing for are pickable (show invisible lines). */
  readonly invisible: boolean;
  /** Lines to leave out (hidden by the preview filters). */
  readonly skip?: (line: LineId) => boolean;
  /** Tolerance in CSS px (default PICK_TOLERANCE). */
  readonly tolerance?: number;
}

/** Lines under a point (device px), topmost first (draw order, then distance). */
export function linesAt(c: OverlayDrawContext, px: number, py: number, opts: LineHitOptions): LineId[] {
  const tol = dp(c, opts.tolerance ?? PICK_TOLERANCE);
  const hits: Array<{ line: LineId; rank: readonly [number, number]; d: number }> = [];
  for (const s of c.lines) {
    if (opts.skip?.(s.line) === true) continue;
    if (s.drawsNothing !== null && !opts.invisible) continue;
    const d = shapeDistance(lineShape(c, s, opts.doc, opts.idx), px, py);
    if (d <= tol) hits.push({ line: s.line, rank: drawRank(opts.doc, s), d });
  }
  hits.sort((a, b) => b.rank[0] - a.rank[0] || b.rank[1] - a.rank[1] || a.d - b.d);
  return hits.map((h) => h.line);
}

/** Corners of note quad `k` in device px. */
export function noteQuadPx(c: OverlayDrawContext, batch: NoteQuadBatch, k: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < 4; i++) {
    const [px, py] = c.worldToPx(batch.pos[k * 8 + i * 2] ?? 0, batch.pos[k * 8 + i * 2 + 1] ?? 0);
    out.push(px, py);
  }
  return out;
}

/** Quad indices of a note in the frame's note batch (holds have several). */
export function quadsOfNote(batch: NoteQuadBatch, note: NoteId): number[] {
  const out: number[] = [];
  const n = Math.min(batch.count, batch.note.length);
  for (let k = 0; k < n; k++) if (batch.note[k] === note) out.push(k);
  return out;
}

/** Quad indices of a set of notes (one pass over the batch). */
export function quadsOfNotes(batch: NoteQuadBatch, notes: ReadonlySet<number>): number[] {
  if (notes.size === 0) return [];
  const out: number[] = [];
  const n = Math.min(batch.count, batch.note.length);
  for (let k = 0; k < n; k++) if (notes.has(batch.note[k] ?? -1)) out.push(k);
  return out;
}

export function pathQuad(ctx: CanvasRenderingContext2D, pts: ArrayLike<number>): void {
  ctx.beginPath();
  ctx.moveTo(pts[0] ?? 0, pts[1] ?? 0);
  for (let i = 1; i < 4; i++) ctx.lineTo(pts[i * 2] ?? 0, pts[i * 2 + 1] ?? 0);
  ctx.closePath();
}

/** Strokes a line shape (dashed when `dash` is set). */
export function strokeShape(
  c: OverlayDrawContext,
  shape: LineShape,
  color: string,
  width: number,
  dash: boolean,
): void {
  const ctx = c.ctx;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = dp(c, width);
  ctx.setLineDash(dash ? [dp(c, 6), dp(c, 4)] : []);
  switch (shape.kind) {
    case 'segment':
      ctx.beginPath();
      ctx.moveTo(shape.a[0], shape.a[1]);
      ctx.lineTo(shape.b[0], shape.b[1]);
      ctx.stroke();
      break;
    case 'quad':
      pathQuad(ctx, shape.pts);
      ctx.stroke();
      break;
    case 'point': {
      const r = dp(c, POINT_RADIUS);
      ctx.beginPath();
      ctx.moveTo(shape.p[0], shape.p[1] - r);
      ctx.lineTo(shape.p[0] + r, shape.p[1]);
      ctx.lineTo(shape.p[0], shape.p[1] + r);
      ctx.lineTo(shape.p[0] - r, shape.p[1]);
      ctx.closePath();
      ctx.stroke();
      break;
    }
  }
  ctx.restore();
}

export interface LabelStyle {
  readonly fg: string;
  readonly bg: string;
  readonly border?: string;
  readonly bold?: boolean;
  /** CSS px font size (default 11). */
  readonly size?: number;
}

type Box = readonly [number, number, number, number];

/** Label boxes drawn so far in one frame (device px): every overlay's labels avoid them (UX-052). */
interface FrameLabels {
  readonly boxes: Box[];
}

const frameLabels = new WeakMap<OverlayDrawContext, FrameLabels>();

function labelsOf(c: OverlayDrawContext): FrameLabels {
  let f = frameLabels.get(c);
  if (f === undefined) {
    f = { boxes: [] };
    frameLabels.set(c, f);
  }
  return f;
}

/** Text widths by font and text: the same labels are measured every frame (measureText dominated the cost). */
const widthCache = new Map<string, number>();
const WIDTH_CACHE_MAX = 4096;

function labelFont(c: Pick<OverlayDrawContext, 'dpr'>, style: LabelStyle): string {
  return `${style.bold === true ? '600 ' : ''}${String(dp(c, style.size ?? 11))}px ${overlayTheme().fontSans || 'sans-serif'}`;
}

/** Size [w, h] (device px) of a label pill. */
export function measureLabel(c: OverlayDrawContext, text: string, style: LabelStyle): readonly [number, number] {
  const font = labelFont(c, style);
  const key = `${font}|${text}`;
  let tw = widthCache.get(key);
  if (tw === undefined) {
    const ctx = c.ctx;
    ctx.save();
    ctx.font = font;
    tw = ctx.measureText(text).width;
    ctx.restore();
    if (widthCache.size >= WIDTH_CACHE_MAX) widthCache.clear();
    widthCache.set(key, tw);
  }
  return [tw + dp(c, 4) * 2, dp(c, style.size ?? 11) + dp(c, 2) * 2];
}

/** Top-left (x, y) kept inside the canvas for a w x h box. */
function clampBox(c: OverlayDrawContext, x: number, y: number, w: number, h: number): readonly [number, number] {
  const W = c.ctx.canvas.width;
  const H = c.ctx.canvas.height;
  return [Math.min(Math.max(0, x), Math.max(0, W - w)), Math.min(Math.max(0, y), Math.max(0, H - h))];
}

function overlaps(a: Box, b: Box): boolean {
  return a[0] < b[0] + b[2] && b[0] < a[0] + a[2] && a[1] < b[1] + b[3] && b[1] < a[1] + a[3];
}

function paintLabel(c: OverlayDrawContext, text: string, box: Box, style: LabelStyle): void {
  const ctx = c.ctx;
  const [bx, by, w, h] = box;
  ctx.save();
  ctx.font = labelFont(c, style);
  ctx.fillStyle = style.bg;
  ctx.globalAlpha = 0.85;
  ctx.beginPath();
  ctx.rect(bx, by, w, h);
  ctx.fill();
  ctx.globalAlpha = 1;
  if (style.border !== undefined) {
    ctx.strokeStyle = style.border;
    ctx.lineWidth = dp(c, 1);
    ctx.stroke();
  }
  ctx.fillStyle = style.fg;
  ctx.textBaseline = 'top';
  ctx.fillText(text, bx + dp(c, 4), by + dp(c, 2));
  ctx.restore();
  labelsOf(c).boxes.push(box);
}

/**
 * Draws a label pill with its top-left near (x, y) (device px), kept inside the canvas, wherever other labels are.
 * Returns its box [x, y, w, h] for hit-testing. Its box is remembered: labels placed later in the frame avoid it.
 */
export function drawLabel(
  c: OverlayDrawContext,
  text: string,
  x: number,
  y: number,
  style: LabelStyle,
): readonly [number, number, number, number] {
  const [w, h] = measureLabel(c, text, style);
  const [bx, by] = clampBox(c, x, y, w, h);
  const box: Box = [bx, by, w, h];
  paintLabel(c, text, box, style);
  return box;
}

/**
 * Places a line label next to an anchor (device px) where it overlaps no label drawn earlier in the frame: right
 * above, right below, left above, left below (greedy, UX-052). Returns the box, or null when every spot is taken
 * (the label is not drawn); `force` draws it at the first spot anyway (the active line is always labelled).
 */
export function placeLabel(
  c: OverlayDrawContext,
  text: string,
  x: number,
  y: number,
  style: LabelStyle,
  force = false,
): readonly [number, number, number, number] | null {
  const [w, h] = measureLabel(c, text, style);
  const gap = dp(c, 8);
  const above = y - h - dp(c, 4);
  const below = y + dp(c, 6);
  const spots: ReadonlyArray<readonly [number, number]> = [
    [x + gap, above],
    [x + gap, below],
    [x - gap - w, above],
    [x - gap - w, below],
  ];
  const taken = labelsOf(c).boxes;
  let first: Box | null = null;
  for (const [sx, sy] of spots) {
    const [bx, by] = clampBox(c, sx, sy, w, h);
    const box: Box = [bx, by, w, h];
    first ??= box;
    if (!taken.some((b) => overlaps(b, box))) {
      paintLabel(c, text, box, style);
      return box;
    }
  }
  if (!force || first === null) return null;
  paintLabel(c, text, first, style);
  return first;
}

/** Label text of a line: "#3 Main". */
export function lineLabel(index: number, name: string): string {
  return name === '' ? `#${String(index)}` : `#${String(index)} ${name}`;
}

/** Data of an overlay hit that carries a hover tooltip (the stage shows it next to the pointer). */
export interface TooltipData {
  readonly tooltip: MessageRef;
}

export function hitTooltip(hit: OverlayHit | null): MessageRef | null {
  const d = hit?.data as Partial<TooltipData> | null | undefined;
  return d !== null && d !== undefined && typeof d === 'object' && d.tooltip !== undefined ? d.tooltip : null;
}

/** Whether a point lies inside a label box. */
export function inBox(box: readonly [number, number, number, number], px: number, py: number): boolean {
  return px >= box[0] && px <= box[0] + box[2] && py >= box[1] && py <= box[1] + box[3];
}
